/* RYDA Landing — vanilla JS
   All interactivity for the Shopify section-based version.
   Uses event delegation + data attributes so sections can be re-rendered by the theme editor.
*/

(function() {
  'use strict';

  /* ─────────── State ─────────── */
  const state = {
    variantId: null,
    variantPrice: 0,     // in cents
    variantComparePrice: 0,
    bundleQty: 1,        // qty multiplier from bundle picker
    bundleDiscount: 0,   // % off applied at checkout via Shopify Automatic Discount
    cartCount: 0,
  };

  const formatMoney = (cents) => {
    try {
      const fmt = window.Shopify && Shopify.currency && Shopify.currency.active;
      return '$' + (cents / 100).toFixed(2);
    } catch(e) {
      return '$' + (cents / 100).toFixed(2);
    }
  };

  /* ─────────── 1. Gallery ─────────── */
  function initGallery(root) {
    const main = root.querySelector('[data-gallery-main]');
    const pager = root.querySelector('[data-gallery-pager]');
    const thumbs = root.querySelectorAll('[data-gallery-thumb]');
    const total = thumbs.length;
    let current = 0;

    const show = (i) => {
      current = i;
      thumbs.forEach((t, k) => t.classList.toggle('active', k === i));
      const src = thumbs[i].dataset.full || thumbs[i].querySelector('img')?.src;
      const label = thumbs[i].dataset.label || '';
      if (main) {
        let img = main.querySelector('img');
        if (!img) {
          img = document.createElement('img');
          img.style.width = '100%';
          img.style.height = '100%';
          img.style.objectFit = 'cover';
          main.appendChild(img);
        }
        if (src) img.src = src;
        img.alt = label;
      }
      if (pager) pager.textContent = String(i+1).padStart(2,'0') + ' / ' + String(total).padStart(2,'0');
    };

    thumbs.forEach((t, i) => t.addEventListener('click', () => show(i)));
    if (thumbs.length) show(0);
  }

  /* ─────────── 2. Variant swatches ─────────── */
  function initSwatches(root) {
    const swatches = root.querySelectorAll('[data-swatch]');
    const label = document.querySelector('[data-variant-label]');

    swatches.forEach(sw => {
      sw.addEventListener('click', () => {
        swatches.forEach(s => s.classList.remove('active'));
        sw.classList.add('active');
        state.variantId = sw.dataset.variantId;
        state.variantPrice = parseInt(sw.dataset.price, 10) || state.variantPrice;
        state.variantComparePrice = parseInt(sw.dataset.comparePrice, 10) || state.variantComparePrice;
        if (label) label.textContent = sw.dataset.name || '';

        // Sync sticky CTA color
        document.querySelectorAll('[data-sticky-thumb]').forEach(t => {
          t.style.background = `repeating-linear-gradient(135deg, ${sw.dataset.color} 0 8px, ${sw.dataset.color}dd 8px 16px)`;
        });
        document.querySelectorAll('[data-sticky-variant]').forEach(t => {
          t.textContent = sw.dataset.name;
        });

        updatePrice();
      });
    });

    // Init from first active swatch
    const initial = root.querySelector('[data-swatch].active') || swatches[0];
    if (initial) initial.click();
  }

  /* ─────────── 3. Bundle picker ─────────── */
  function initBundles(root) {
    const cards = root.querySelectorAll('[data-bundle]');
    cards.forEach(card => {
      card.addEventListener('click', () => {
        cards.forEach(c => c.classList.remove('active'));
        card.classList.add('active');
        state.bundleQty = parseInt(card.dataset.qty, 10) || 1;
        state.bundleDiscount = parseFloat(card.dataset.discountPct) || 0;
        document.querySelectorAll('[data-sticky-bundle]').forEach(el => {
          el.textContent = card.dataset.label || '';
        });
        updatePrice();
      });
    });
    const active = root.querySelector('[data-bundle].active') || cards[0];
    if (active) active.click();
  }

  /* ─────────── 4. Price total update ─────────── */
  function updatePrice() {
    const subtotal = state.variantPrice * state.bundleQty;
    const total = Math.round(subtotal * (1 - state.bundleDiscount / 100));
    document.querySelectorAll('[data-cta-total]').forEach(el => {
      el.textContent = formatMoney(total);
    });
  }

  /* ─────────── 5. Add to cart ─────────── */
  function addToCart(btn) {
    if (!state.variantId) {
      console.warn('[RYDA] No variant selected');
      return;
    }
    const original = btn.innerHTML;
    btn.disabled = true;
    btn.classList.add('is-loading');

    const formData = {
      items: [{
        id: parseInt(state.variantId, 10),
        quantity: state.bundleQty,
      }],
    };

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(formData),
    })
    .then(r => r.json())
    .then(data => {
      if (data.status && data.status !== 200) throw new Error(data.description || 'Add to cart failed');
      showToast(`${state.bundleQty} towel${state.bundleQty > 1 ? 's' : ''} added · ${formatMoney(Math.round(state.variantPrice * state.bundleQty * (1 - state.bundleDiscount / 100)))}`);
      return fetch(window.Shopify.routes.root + 'cart.js');
    })
    .then(r => r && r.json())
    .then(cart => {
      if (cart && typeof cart.item_count === 'number') {
        state.cartCount = cart.item_count;
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = ' (' + cart.item_count + ')';
        });
      }
    })
    .catch(err => {
      console.error(err);
      showToast('Couldn\'t add — please try again');
    })
    .finally(() => {
      btn.disabled = false;
      btn.classList.remove('is-loading');
      btn.innerHTML = original;
    });
  }

  /* ─────────── 6. Toast ─────────── */
  let toastTimer;
  function showToast(msg) {
    const toast = document.querySelector('[data-toast]');
    if (!toast) return;
    toast.querySelector('[data-toast-msg]').textContent = msg;
    toast.classList.add('visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('visible'), 2800);
  }

  /* ─────────── 7. Sticky CTA on scroll ─────────── */
  function initStickyCta() {
    const bar = document.querySelector('[data-sticky-cta]');
    if (!bar) return;
    const threshold = 700;
    const onScroll = () => bar.classList.toggle('visible', window.scrollY > threshold);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ─────────── 8. Anatomy hotspots ─────────── */
  function initAnatomy() {
    const hotspots = document.querySelectorAll('[data-hotspot]');
    const cards = document.querySelectorAll('[data-detail-card]');
    const setActive = (id) => {
      hotspots.forEach(h => h.classList.toggle('active', h.dataset.hotspot === id));
      cards.forEach(c => c.classList.toggle('active', c.dataset.detailCard === id));
    };
    hotspots.forEach(h => h.addEventListener('click', () => setActive(h.dataset.hotspot)));
    cards.forEach(c => c.addEventListener('click', () => setActive(c.dataset.detailCard)));
    const first = hotspots[0];
    if (first) setActive(first.dataset.hotspot);
  }

  /* ─────────── 9. Before / After slider ─────────── */
  function initBeforeAfter() {
    document.querySelectorAll('[data-ba]').forEach(slider => {
      const before = slider.querySelector('[data-ba-before]');
      const handle = slider.querySelector('[data-ba-handle]');
      const tagB = slider.querySelector('[data-ba-tag-b]');
      const tagA = slider.querySelector('[data-ba-tag-a]');
      let dragging = false;
      let pos = 50;

      const move = (clientX) => {
        const r = slider.getBoundingClientRect();
        pos = Math.min(100, Math.max(0, ((clientX - r.left) / r.width) * 100));
        if (before) before.style.clipPath = `inset(0 ${100 - pos}% 0 0)`;
        if (handle) handle.style.left = pos + '%';
        if (tagB) tagB.style.display = pos > 12 ? 'block' : 'none';
        if (tagA) tagA.style.display = pos < 88 ? 'block' : 'none';
      };

      slider.addEventListener('mousedown', e => { dragging = true; move(e.clientX); });
      slider.addEventListener('touchstart', e => {
        dragging = true;
        if (e.touches[0]) move(e.touches[0].clientX);
      }, { passive: true });

      window.addEventListener('mousemove', e => { if (dragging) move(e.clientX); });
      window.addEventListener('touchmove', e => {
        if (dragging && e.touches[0]) move(e.touches[0].clientX);
      }, { passive: true });
      window.addEventListener('mouseup', () => dragging = false);
      window.addEventListener('touchend', () => dragging = false);
    });
  }

  /* ─────────── 10. FAQ accordion ─────────── */
  function initFaq() {
    document.querySelectorAll('[data-faq-item]').forEach(item => {
      const btn = item.querySelector('[data-faq-q]');
      if (!btn) return;
      btn.addEventListener('click', () => {
        const wasOpen = item.classList.contains('open');
        item.parentElement.querySelectorAll('[data-faq-item].open').forEach(o => o.classList.remove('open'));
        if (!wasOpen) item.classList.add('open');
      });
    });
  }

  /* ─────────── 11. Fade-in on scroll ─────────── */
  function initFadeIn() {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('visible');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.08 });
    document.querySelectorAll('.fade-in').forEach(el => io.observe(el));
  }

  /* ─────────── 12. Init cart count from /cart.js ─────────── */
  function initCart() {
    if (!window.Shopify || !window.Shopify.routes) return;
    fetch(window.Shopify.routes.root + 'cart.js')
      .then(r => r.json())
      .then(cart => {
        state.cartCount = cart.item_count;
        document.querySelectorAll('[data-cart-count]').forEach(el => {
          el.textContent = ' (' + cart.item_count + ')';
        });
      })
      .catch(() => {});
  }

  /* ─────────── Boot ─────────── */
  function boot() {
    document.querySelectorAll('[data-ryda-gallery]').forEach(initGallery);
    document.querySelectorAll('[data-ryda-swatches]').forEach(initSwatches);
    document.querySelectorAll('[data-ryda-bundles]').forEach(initBundles);
    document.querySelectorAll('[data-ryda-add]').forEach(btn => {
      btn.addEventListener('click', () => addToCart(btn));
    });
    initStickyCta();
    initAnatomy();
    initBeforeAfter();
    initFaq();
    initFadeIn();
    initCart();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when Shopify theme editor reloads a section
  document.addEventListener('shopify:section:load', boot);
})();